import React, { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";

/**
 * WatchlistAddSearch (Phase 8.WLNAV.v1)
 * ─────────────────────────────────────
 * Dedicated search-and-add control for the Watchlist page. Uses the same
 * lightweight `/api/search` autocomplete as the global search, but selecting
 * a result calls `POST /api/watchlist` instead of `/api/analyze`.
 *
 * Guarantees:
 *   • never calls /api/analyze
 *   • never consumes an analysis quota
 *   • never triggers an LLM call
 *   • never generates AI content
 *   • deduplicates via the backend's existing 409 response
 *   • toast success shows an optional "Analyze now" secondary action —
 *     only that button ever navigates to /analyze/{ticker}.
 *
 * The autocomplete-fetching endpoint (/api/search) does NOT trigger an
 * analysis pipeline; it only queries FMP's cheap search/search-ticker
 * endpoints for ticker+company suggestions.
 */
export default function WatchlistAddSearch({ onAdded, onLimitHit }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const abortRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close autocomplete when the user clicks anywhere outside the widget.
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Debounced autocomplete lookup.
  useEffect(() => {
    if (!q || q.trim().length < 1) { setResults([]); return; }
    const term = q.trim();
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.get(`/search`, { params: { q: term, limit: 8 }, signal: ctrl.signal });
        const items = r.data?.items || r.data?.results || r.data || [];
        setResults(Array.isArray(items) ? items.slice(0, 8) : []);
        setHighlight(0);
      } catch (e) {
        if (!ctrl.signal.aborted) setResults([]);
      } finally {
        if (!ctrl.signal.aborted) setBusy(false);
      }
    }, 180);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [q]);

  const addTicker = async (ticker, name) => {
    if (!ticker) return;
    const T = String(ticker).toUpperCase().trim();
    setAdding(true);
    try {
      // POST /api/watchlist — server-side dedupe + quota enforcement.
      // Payload intentionally minimal: no analysis is generated at this
      // point. The Watchlist row will show "Not analyzed yet" placeholders
      // until the user explicitly runs an analysis.
      await api.post(`/watchlist`, { ticker: T, name: name || undefined });
      // Success toast with an optional "Analyze now" secondary action.
      toast.success(t("watchlist.add_success", { ticker: T }) || `${T} added to your Watchlist.`, {
        action: {
          label: t("watchlist.add_success_analyze") || "Analyze now",
          onClick: () => { window.location.href = `/analyze/${T}`; },
        },
      });
      setQ(""); setResults([]); setOpen(false);
      onAdded && onAdded(T);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? (detail.error || detail.error_code || detail.code) : null;
      if (status === 409 || code === "already_in_watchlist" || (typeof detail === "string" && /already/i.test(detail))) {
        toast(t("watchlist.add_already") || "Already in your Watchlist.");
      } else if (status === 402 || status === 403 || code === "watchlist_limit_reached") {
        toast.error(t("watchlist.limit_toast") || "Watchlist limit reached.");
        onLimitHit && onLimitHit();
      } else if (status === 400 || code === "invalid_ticker") {
        // Phase 8.LP.LR v1.5 — surface the specific "ticker shape
        // was invalid" case so the user knows the problem is with
        // the symbol format, not the service being down.
        toast.error(t("watchlist.add_invalid") || "This ticker could not be added.");
      } else {
        toast.error(t("watchlist.add_error") || "Could not add to Watchlist.");
      }
    } finally {
      setAdding(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((i) => Math.min(results.length - 1, i + 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (results.length > 0 && open) {
        const r = results[highlight] || results[0];
        addTicker(r.ticker || r.symbol, r.name || r.company_name);
      } else if (q.trim()) {
        // Fallback: try to add the raw typed value as a ticker.
        addTicker(q.trim().toUpperCase());
      }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="watchlist-add-search-wrap">
      <label className="sr-only" htmlFor="watchlist-add-search">
        {t("watchlist.add_a11y_label") || "Add a stock by ticker or company name"}
      </label>
      <div className="relative">
        <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          id="watchlist-add-search"
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          disabled={adding}
          data-testid="watchlist-add-search-input"
          className="w-full h-10 pl-9 pr-10 rounded-md bg-card border border-border/70 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          // Desktop placeholder uses the longer copy; the hidden mobile
          // placeholder swap is handled via a CSS class below.
          placeholder={t("watchlist.add_placeholder_desktop") || "Add stock by ticker or company"}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="watchlist-add-search-listbox"
        />
        {busy || adding ? (
          <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />
        ) : (
          <Plus className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        )}
      </div>
      {open && results.length > 0 ? (
        <ul id="watchlist-add-search-listbox" role="listbox" data-testid="watchlist-add-search-listbox"
            className="absolute z-30 mt-1 w-full rounded-md border border-border/70 bg-card shadow-lg max-h-72 overflow-auto">
          {results.map((r, i) => {
            const T = (r.ticker || r.symbol || "").toUpperCase();
            const name = r.name || r.company_name || "";
            return (
              <li
                key={`${T}-${i}`}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={(e) => { e.preventDefault(); addTicker(T, name); }}
                onMouseEnter={() => setHighlight(i)}
                className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${i === highlight ? "bg-secondary/60" : "hover:bg-secondary/40"}`}
                data-testid={`watchlist-add-search-result-${T}`}
              >
                <span className="font-mono font-semibold shrink-0 w-16">{T}</span>
                <span className="truncate text-muted-foreground">{name}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
